/**
 * ScoringOverrideDialog
 *
 * A dialog that lets the user modify scoring configuration parameters for a
 * single recalculation run. Overrides are NOT persisted — they are only sent
 * as the request body to the Engine API's recalculation endpoint.
 */
import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  TextField,
  CircularProgress,
  Slider,
  Divider,
  Alert,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { scoringSeverityOptions } from "../../services/constants";
import { computeInitialModified } from "../../utils/scoringOverrideDialogModified";
import {
  CATEGORY_WEIGHT_SUM_MAX_MESSAGE,
  COMPARISON_CATEGORY_WEIGHT_KEYS,
  IMPACT_SUM_MAX_MESSAGE,
  isComparisonCategoryWeightTotalValid,
  validateScoringValues,
} from "../../utils/scoringConfigFormValidation";

/** Scoring type — determines which default set and labels to use */
export type ScoringType = "validation" | "comparison" | "generic_rq";

/** Parameter descriptor for rendering editable fields */
export interface ParamDescriptor {
  key: string;
  label: string;
  min: number;
  max?: number;
  step: number;
}

export const VALIDATION_PARAMS: ParamDescriptor[] = [
  {
    key: "impact_of_record_level",
    label: "Impact of Record Level",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: "impact_of_issue_level",
    label: "Impact of Issue Level",
    min: 0,
    max: 1,
    step: 0.05,
  },
  { key: "factor_low", label: "Factor Low", min: 0, max: 1, step: 0.05 },
  { key: "factor_medium", label: "Factor Medium", min: 0, max: 1, step: 0.05 },
  { key: "factor_high", label: "Factor High", min: 0, max: 1, step: 0.05 },
];

export const COMPARISON_PARAMS: ParamDescriptor[] = [
  // DQ-8521: Blend Ratio is a capped weight in [0, 1]. It is distinct from
  // `missing_factor`, which stays an uncapped amplification multiplier.
  { key: "blend_ratio", label: "Blend Ratio", min: 0, max: 1, step: 0.05 },
  {
    key: "category_weight_missing",
    label: "Category Weight — Missing",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: "category_weight_mismatch",
    label: "Category Weight — Mismatch",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: "category_weight_validation",
    label: "Category Weight — Validation",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: "missing_factor",
    label: "Missing Factor",
    min: 0,
    step: 0.05,
  },
  {
    key: "mismatch_factor_low",
    label: "Mismatch Factor Low",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: "mismatch_factor_medium",
    label: "Mismatch Factor Medium",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: "mismatch_factor_high",
    label: "Mismatch Factor High",
    min: 0,
    max: 1,
    step: 0.05,
  },
];

// GRQ uses the same params as validation
export const GRQ_PARAMS: ParamDescriptor[] = VALIDATION_PARAMS;

export const PARAMS_MAP: Record<ScoringType, ParamDescriptor[]> = {
  validation: VALIDATION_PARAMS,
  comparison: COMPARISON_PARAMS,
  generic_rq: GRQ_PARAMS,
};

/** Scoring types that accept a severity_mappings dictionary in the override payload */
export const SEVERITY_MAPPINGS_TYPES: ReadonlySet<ScoringType> =
  new Set<ScoringType>(["comparison", "generic_rq"]);

/** Placeholder label for the check/query name input per type */
export const SEVERITY_MAPPING_NAME_LABEL: Record<ScoringType, string> = {
  validation: "",
  comparison: "Check Name",
  generic_rq: "Query Name",
};

/**
 * Strip empty / placeholder rows from a severity_mappings dictionary so the
 * payload only contains real user-entered name → severity pairs.
 * Exported for reuse by execution-time override Accordion.
 */
export const cleanMappings = (
  src: Record<string, string>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(src).filter(([k]) => k !== "" && !k.startsWith("__new_")),
  );

interface IScoringOverrideDialogProps {
  open: boolean;
  onClose: () => void;
  type: ScoringType;
  /** Current config_snapshot from the latest score (used as initial values) */
  currentConfig: Record<string, any> | null;
  /** Static fallback defaults when no snapshot is available */
  fallbackDefaults: Record<string, any>;
  /** Whether a recalculation is in progress */
  recalculating: boolean;
  /** Called with the override config (or empty object if unchanged) */
  onRecalculate: (configOverride: Record<string, any>) => void;
}

const TITLE_MAP: Record<ScoringType, string> = {
  validation: "Validation Scoring",
  comparison: "Comparison Scoring",
  generic_rq: "Generic RQ Scoring",
};

const ScoringOverrideDialog: React.FC<IScoringOverrideDialogProps> = ({
  open,
  onClose,
  type,
  currentConfig,
  fallbackDefaults,
  recalculating,
  onRecalculate,
}) => {
  const params = PARAMS_MAP[type];
  const supportsSeverityMappings = SEVERITY_MAPPINGS_TYPES.has(type);
  const baseConfig =
    currentConfig && typeof currentConfig === "object"
      ? currentConfig
      : fallbackDefaults;

  const [values, setValues] = useState<Record<string, number>>({});
  /**
   * Mirrors the raw string the user has typed in each TextField so the input
   * can be fully cleared without snapping back (same pattern as DQ-8521 fix).
   * Sliders keep this in sync by writing String(val) on every change.
   */
  const [rawValues, setRawValues] = useState<Record<string, string>>({});
  const [severityMappings, setSeverityMappings] = useState<
    Record<string, string>
  >({});
  const [modified, setModified] = useState(false);
  /** Per-field range validation errors — set when user types a value outside [p.min, p.max]. */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  /** Cross-field error: impact_of_record_level + impact_of_issue_level must not exceed 1.0. */
  const [sumError, setSumError] = useState<string>("");
  /** Cross-field error: the three comparison category weights must not exceed 1.0. */
  const [categoryWeightError, setCategoryWeightError] = useState<string>("");
  const newMappingCounter = useRef(0);

  /**
   * True for scoring types that carry both impact fields whose sum is bounded.
   * Validation and Generic-RQ both expose the pair; Comparison does not.
   */
  const supportsImpactSumConstraint =
    params.some((p) => p.key === "impact_of_record_level") &&
    params.some((p) => p.key === "impact_of_issue_level");

  /**
   * DQ-8521: the category-weight total rule is Comparison-only. Validation and
   * Generic-RQ do not expose these fields and must not inherit the constraint.
   */
  const supportsCategoryWeightConstraint =
    COMPARISON_CATEGORY_WEIGHT_KEYS.every((key) =>
      params.some((p) => p.key === key),
    );

  const buildInitialMappings = (): Record<string, string> => {
    if (!supportsSeverityMappings) return {};
    const src =
      (baseConfig as any)?.severity_mappings &&
      typeof (baseConfig as any).severity_mappings === "object"
        ? (baseConfig as any).severity_mappings
        : {};
    return { ...src };
  };

  // Reset values when dialog opens or config changes.
  // DQ-8533: initialise `modified` by comparing baseConfig against fallbackDefaults
  // so the "Reset to Defaults" button is enabled whenever the loaded config
  // already differs from the system defaults (not only after in-session edits).
  // DQ-8540 (re-open) Fix #2: validate initial values immediately on open so
  // that any out-of-range values stored in currentConfig are flagged before the
  // user interacts with the dialog (previously errors only appeared after the
  // user manually changed a field, allowing invalid stored values to be submitted).
  useEffect(() => {
    if (open) {
      const initial: Record<string, number> = {};
      const rawInitial: Record<string, string> = {};
      params.forEach((p) => {
        const v = baseConfig[p.key] ?? fallbackDefaults[p.key] ?? 0;
        initial[p.key] = v;
        rawInitial[p.key] = String(v);
      });
      const initialMappings = buildInitialMappings();
      setValues(initial);
      setRawValues(rawInitial);
      setSeverityMappings(initialMappings);
      setModified(
        computeInitialModified(
          params,
          baseConfig,
          fallbackDefaults,
          supportsSeverityMappings,
        ),
      );

      const initialValidation = validateScoringValues(params, initial);
      setFieldErrors(initialValidation.fieldErrors);
      setSumError(initialValidation.impactSumError ?? "");
      setCategoryWeightError(initialValidation.categoryWeightSumError ?? "");

      newMappingCounter.current = 0;
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * DQ-8540 Bug #1 fix: validate typed numeric value against the param's
   * [min, max] range and surface an error message when out of bounds.
   * Always pass the ParamDescriptor so the slider can also clear stale errors.
   * Also enforces the cross-field sum constraint on impact fields.
   */
  const handleChange = (key: string, val: number, param: ParamDescriptor) => {
    const nextValues = { ...values, [key]: val };
    setValues(nextValues);
    setModified(true);

    // Per-field range check
    if (
      val < param.min ||
      (param.max !== undefined && val > param.max)
    ) {
      setFieldErrors((prev) => ({
        ...prev,
        [key]:
          param.max === undefined
            ? `${param.label} must be at least ${param.min}`
            : `${param.label} must be between ${param.min} and ${param.max}`,
      }));
    } else {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }

    // Cross-field sum constraint: impact_of_record_level + impact_of_issue_level ≤ 1.0
    if (
      supportsImpactSumConstraint &&
      (key === "impact_of_record_level" || key === "impact_of_issue_level")
    ) {
      // Use the incoming val for the changed key; read the sibling from current `values`
      const record =
        key === "impact_of_record_level"
          ? val
          : (values["impact_of_record_level"] ?? 0);
      const issue =
        key === "impact_of_issue_level"
          ? val
          : (values["impact_of_issue_level"] ?? 0);
      const sum = record + issue;
      if (sum > 1.0 + 1e-9) {
        setSumError(IMPACT_SUM_MAX_MESSAGE);
      } else {
        setSumError("");
      }
    }

    // Cross-field aggregate: missing + mismatch + validation weights ≤ 1.0
    if (
      supportsCategoryWeightConstraint &&
      (COMPARISON_CATEGORY_WEIGHT_KEYS as readonly string[]).includes(key)
    ) {
      setCategoryWeightError(
        isComparisonCategoryWeightTotalValid(nextValues, "max")
          ? ""
          : CATEGORY_WEIGHT_SUM_MAX_MESSAGE,
      );
    }
  };

  // DQ-8533: reset to true system fallback defaults (not baseConfig/currentConfig).
  // Previously this reset to baseConfig which was a no-op when the dialog had
  // just opened (values were already seeded from baseConfig), and the button
  // would remain enabled with no visible effect.
  const handleResetDefaults = () => {
    const initial: Record<string, number> = {};
    const rawInitial: Record<string, string> = {};
    params.forEach((p) => {
      const v = fallbackDefaults[p.key] ?? 0;
      initial[p.key] = v;
      rawInitial[p.key] = String(v);
    });
    setValues(initial);
    setRawValues(rawInitial);
    setSeverityMappings({});
    setModified(false);
    setFieldErrors({});
    setSumError("");
    setCategoryWeightError("");
  };

  const handleAddMapping = () => {
    const tempKey = `__new_${newMappingCounter.current++}`;
    setSeverityMappings((prev) => ({ ...prev, [tempKey]: "medium" }));
    setModified(true);
  };

  const handleRemoveMapping = (name: string) => {
    setSeverityMappings((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setModified(true);
  };

  const handleUpdateMappingName = (oldName: string, newName: string) => {
    if (newName === oldName) return;
    setSeverityMappings((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([k, v]) => {
        next[k === oldName ? newName : k] = v;
      });
      return next;
    });
    setModified(true);
  };

  const handleUpdateMappingSeverity = (name: string, severity: string) => {
    setSeverityMappings((prev) => ({ ...prev, [name]: severity }));
    setModified(true);
  };

  const hasInvalidMappingRow = supportsSeverityMappings
    ? Object.keys(severityMappings).some(
        (k) => k === "" || k.startsWith("__new_"),
      )
    : false;

  /**
   * True when any numeric field has a range/required error, the impact total
   * exceeds 1, or the comparison category-weight total exceeds 1. This is the
   * single predicate shared by the Recalculate button's disabled state and the
   * imperative click handler (DQ-8521).
   */
  const hasFieldErrors =
    Object.keys(fieldErrors).length > 0 ||
    Boolean(sumError) ||
    Boolean(categoryWeightError);

  const handleRecalculate = () => {
    if (hasFieldErrors || hasInvalidMappingRow || recalculating) return;
    // Always send the full flat set of current values as the override payload
    // (matches the schema in docs/Payload/override_payload.json for
    // /scoring/{validate|compare|research-query}/{execution_id}).
    // The values state is initialised from currentConfig (or fallbackDefaults)
    // when the dialog opens, so this correctly reflects the current
    // configuration even when the user has not manually adjusted any parameter.
    const override: Record<string, any> = {};
    params.forEach((p) => {
      override[p.key] = values[p.key];
    });
    if (supportsSeverityMappings) {
      override.severity_mappings = cleanMappings(severityMappings);
    }
    onRecalculate(override);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}
      >
        <TuneIcon sx={{ color: "text.secondary" }} />
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Recalculate — {TITLE_MAP[type]}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Adjust parameters for this recalculation only. Changes are not
            saved.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {!modified && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Using current configuration. Modify any parameter below to override
            for this run.
          </Alert>
        )}
        {modified && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Parameters modified — these overrides will only apply to this single
            recalculation.
          </Alert>
        )}
        {params.map((p) => (
          <Box key={p.key} sx={{ mb: 2.5 }}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 0.75 }}>
              <Grid item xs sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  title={p.label}
                >
                  {p.label}
                </Typography>
              </Grid>
              <Grid item xs="auto">
                <TextField
                  size="small"
                  type="number"
                  // Use rawValues so the field can be fully cleared without
                  // snapping back (DQ-8521 pattern).
                  value={rawValues[p.key] ?? ""}
                  onChange={(e) => {
                    const rawStr = e.target.value;
                    setRawValues((prev) => ({ ...prev, [p.key]: rawStr }));
                    if (rawStr === "") {
                      // Field cleared — block recalculation until re-filled
                      setModified(true);
                      setFieldErrors((prev) => ({
                        ...prev,
                        [p.key]: "Value is required",
                      }));
                      // Clear any stale sum error for impact fields
                      if (
                        supportsImpactSumConstraint &&
                        (p.key === "impact_of_record_level" ||
                          p.key === "impact_of_issue_level")
                      ) {
                        setSumError("");
                      }
                      if (
                        supportsCategoryWeightConstraint &&
                        (
                          COMPARISON_CATEGORY_WEIGHT_KEYS as readonly string[]
                        ).includes(p.key)
                      ) {
                        setCategoryWeightError("");
                      }
                      return;
                    }
                    const v = parseFloat(rawStr);
                    if (!isNaN(v)) handleChange(p.key, v, p);
                  }}
                  error={Boolean(fieldErrors[p.key])}
                  inputProps={{
                    step: p.step,
                    style: {
                      width: 72,
                      textAlign: "right",
                      fontFamily: "monospace",
                    },
                  }}
                />
              </Grid>
            </Grid>
            {fieldErrors[p.key] && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.25 }}
              >
                {fieldErrors[p.key]}
              </Typography>
            )}
            <Slider
              value={values[p.key] ?? 0}
              min={p.min}
              max={p.max ?? Math.max(values[p.key] ?? 1, 5)}
              step={p.step}
              onChange={(_, val) => {
                const v = val as number;
                // Sync rawValues so the TextField reflects the slider position
                setRawValues((prev) => ({ ...prev, [p.key]: String(v) }));
                handleChange(p.key, v, p);
              }}
              size="small"
            />
          </Box>
        ))}

        {sumError && (
          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
            {sumError}
          </Alert>
        )}

        {categoryWeightError && (
          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
            {categoryWeightError}
          </Alert>
        )}

        {supportsSeverityMappings && (
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="body2" fontWeight={700}>
                Severity Mappings
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddMapping}
                disabled={recalculating}
              >
                Add Mapping
              </Button>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Per-{type === "comparison" ? "check" : "query"} severity overrides
              applied to this recalculation only.
            </Typography>
            {Object.keys(severityMappings).length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic", py: 1 }}
              >
                No severity mappings defined.
              </Typography>
            ) : (
              <TableContainer
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        {SEVERITY_MAPPING_NAME_LABEL[type]}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          width: 140,
                        }}
                      >
                        Severity
                      </TableCell>
                      <TableCell align="center" sx={{ width: 48, p: 0.5 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(severityMappings).map(
                      ([name, severity]) => {
                        const isTemp = name.startsWith("__new_");
                        return (
                          <TableRow key={name}>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                placeholder={SEVERITY_MAPPING_NAME_LABEL[type]}
                                value={isTemp ? "" : name}
                                onChange={(e) =>
                                  handleUpdateMappingName(name, e.target.value)
                                }
                                disabled={recalculating}
                              />
                            </TableCell>
                            <TableCell sx={{ width: 140 }}>
                              <Select
                                size="small"
                                fullWidth
                                value={severity}
                                onChange={(e) =>
                                  handleUpdateMappingSeverity(
                                    name,
                                    e.target.value as string,
                                  )
                                }
                                disabled={recalculating}
                              >
                                {scoringSeverityOptions.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ width: 48, p: 0.5 }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveMapping(name)}
                                disabled={recalculating}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      },
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {hasInvalidMappingRow && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Please provide a name for every severity mapping row before
                recalculating.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        className="dialog-footer"
        sx={{ px: 3, py: 1.5, justifyContent: "space-between" }}
      >
        <Button
          onClick={handleResetDefaults}
          disabled={!modified || recalculating}
          className="btn-orange btn-border"
        >
          Reset to Defaults
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={recalculating}
            className="btn-orange btn-border"
          >
            Cancel
          </Button>
          <Button
            className="btn-orange btn-dark"
            startIcon={
              recalculating ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <RefreshIcon />
              )
            }
            disabled={recalculating || hasInvalidMappingRow || hasFieldErrors}
            onClick={handleRecalculate}
          >
            {recalculating
              ? "Recalculating…"
              : modified
                ? "Recalculate with Overrides"
                : "Recalculate Score"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ScoringOverrideDialog;
